/**
 * Community detail presentation. The shared workspace owns requests, persisted
 * observations, staged addresses and recovery; forms own only their drafts.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Building2, Key, MapPin, Clock, Shield, Pencil, Trash2, AlertTriangle,
  Plus, X, Search, Loader2, KeyRound, DoorClosed, Crosshair, UserRound,
} from 'lucide-react';
import {
  googlePlacesAutocompleteAllowed,
  mountPlacesField,
  PLACE_SELECTION,
} from '@/utils/googlePlacesAutocomplete';
import CommunityMap from './CommunityMap';

// ── helpers ─────────────────────────────────────────────────────────────────

function CodeTypeBadge({ type }) {
  const label = type === 'gate' ? 'Gate' : 'Door';
  const cls = type === 'gate'
    ? 'bg-amber-50 text-amber-700 border border-amber-200'
    : 'bg-blue-50 text-blue-700 border border-blue-200';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

function relativeTime(iso) {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  const mo = Math.floor(days / 30);
  return mo < 12 ? `${mo}mo ago` : `${Math.floor(mo / 12)}y ago`;
}

// ── Address grouping (by street) ────────────────────────────────────────────
// Parses a Google-style formatted_address ("2546 Stonequist Ave, Henderson,
// NV 89052, USA") and splits it into a street-name bucket plus a numeric
// house number so the addresses list can group + sort sensibly.
//
//   - Bucket key: the street portion ("Stonequist Ave"), used as the group
//     header. Comparison is case-insensitive.
//   - Sort within bucket: numeric on the house number ("2546"), with the
//     suffix (e.g. "A" in "2546A") as a secondary tiebreaker.
//   - Falls back gracefully: addresses with no formatted_address go to
//     "Unresolved" (sorted last), addresses with no leading number sort by
//     the head string itself.
function parseAddressForGrouping(formatted) {
  if (!formatted) {
    return { street: 'Unresolved', number: null, suffix: '', head: '' };
  }
  const firstComma = formatted.indexOf(',');
  const head = (firstComma >= 0 ? formatted.slice(0, firstComma) : formatted).trim();
  // "2546 Stonequist Ave" → number=2546, suffix='', street="Stonequist Ave"
  // "2546A Stonequist Ave" → number=2546, suffix='A', street="Stonequist Ave"
  const m = head.match(/^(\d+)([A-Za-z]?)\s+(.+)$/);
  if (m) {
    return {
      street: m[3].trim(),
      number: parseInt(m[1], 10),
      suffix: m[2] || '',
      head,
    };
  }
  // No leading number — treat the whole head as the street.
  return { street: head, number: null, suffix: '', head };
}

function groupAddressesByStreet(addresses) {
  const buckets = new Map();
  for (const a of addresses) {
    const meta = parseAddressForGrouping(a.formatted_address);
    const key = meta.street;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push({ ...a, _meta: meta });
  }
  // Sort within each bucket
  for (const arr of buckets.values()) {
    arr.sort((x, y) => {
      // Numeric house numbers first, ascending
      if (x._meta.number != null && y._meta.number != null) {
        if (x._meta.number !== y._meta.number) return x._meta.number - y._meta.number;
        return x._meta.suffix.localeCompare(y._meta.suffix);
      }
      // One side has no number — put numbered entries first
      if (x._meta.number != null) return -1;
      if (y._meta.number != null) return 1;
      // Both unnumbered — alpha on the head
      return x._meta.head.localeCompare(y._meta.head, undefined, { sensitivity: 'base' });
    });
  }
  // Sort the buckets alphabetically by street name, with "Unresolved" last.
  const entries = Array.from(buckets.entries());
  entries.sort(([a], [b]) => {
    if (a === 'Unresolved' && b !== 'Unresolved') return 1;
    if (b === 'Unresolved' && a !== 'Unresolved') return -1;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  });
  return entries; // [ [streetName, [...addresses]], ... ]
}

// ── blank canvas ─────────────────────────────────────────────────────────────

function BlankCanvas() {
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center px-8" data-testid="community-detail-empty">
      <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center mb-4">
        <Building2 className="h-6 w-6 text-stone-400" />
      </div>
      <p className="text-sm font-medium text-stone-500">Select a community to inspect it</p>
      <p className="text-xs text-stone-400 mt-1">Codes, linked addresses, and a map will appear here.</p>
    </div>
  );
}

// ── Address search bar (Slice 3) ─────────────────────────────────────────────
//
// A self-contained Google Places Autocomplete that emits a pending-pin
// candidate when a user picks a suggestion. Loads the Maps JS Places library
// lazily and reuses any already-loaded script.

function AddressSearchBar({ existingPlaceIds, pendingPlaceIds, onPick, disabled, captureSelection, isCurrent }) {
  const hostRef = useRef(null);
  const fieldRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadState, setLoadState] = useState('loading');
  const placesAllowed = googlePlacesAutocompleteAllowed();
  const onPickRef = useRef(onPick);
  const selection = useRef(null);
  if (!selection.current) selection.current = captureSelection();
  const owns = useRef(isCurrent);
  owns.current = isCurrent;
  useEffect(() => { onPickRef.current = onPick; }, [onPick]);

  // Refs so the selection callback always reads fresh sets
  const existingRef = useRef(existingPlaceIds);
  const pendingRef = useRef(pendingPlaceIds);
  useEffect(() => { existingRef.current = existingPlaceIds; }, [existingPlaceIds]);
  useEffect(() => { pendingRef.current = pendingPlaceIds; }, [pendingPlaceIds]);

  const placeholder = placesAllowed
    ? (isLoaded ? 'Search an address to stage…' : loadState === 'unavailable' ? 'Address search unavailable' : 'Loading…')
    : 'Address search unavailable offline';

  // Mounted once; later prop changes ride the handle's setters. The seam owns
  // the field because the replacement provider supplies its own element.
  useEffect(() => {
    let active = true;
    const field = mountPlacesField(hostRef.current, {
      kind: PLACE_SELECTION,
      placeholder,
      disabled: true,
      className: 'w-full pl-8 pr-2 py-1.5 text-xs border border-stone-300 rounded-md outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-stone-50 disabled:text-stone-400',
      testId: 'community-address-search-input',
      onReadyChange: setIsLoaded,
      onLoadStateChange: setLoadState,
      onSelect: (place) => {
        if (!active || !owns.current(selection.current)) return;
        // Already saved or already staged: the seam has cleared the field.
        if (existingRef.current?.has(place.placeId) || pendingRef.current?.has(place.placeId)) return;
        onPickRef.current?.({
          place_id: place.placeId,
          formatted_address: place.formattedAddress,
          lat: place.latitude,
          lng: place.longitude,
        }, selection.current);
      },
    });
    fieldRef.current = field;
    return () => {
      active = false;
      field?.destroy();
      fieldRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Address search stays disabled until the provider is ready, and stays
  // disabled entirely on an offline desktop.
  useEffect(() => {
    fieldRef.current?.setDisabled(disabled || !isLoaded || !placesAllowed);
  }, [disabled, isLoaded, placesAllowed]);
  useEffect(() => { fieldRef.current?.setPlaceholder(placeholder); }, [placeholder]);

  return (
    <div className="relative" ref={hostRef}>
      <Search className="absolute left-2.5 top-[15px] -translate-y-1/2 h-3.5 w-3.5 text-stone-400 pointer-events-none z-10" />
    </div>
  );
}

// ── inline "Add Code" form ──────────────────────────────────────────────────
// Renders below the Codes header when the user clicks "+ Add Code". Same shape
// as the new-community form in AccessCodesPanel, minus the community-name
// field (the community already exists). The shared workspace binds creation
// to the selected community identity and owns the request/recovery lifecycle.

function AddCodeForm({ existingCodes, onCancel, onCreated, workspace }) {
  const { registerDirty } = workspace;
  const [type, setType] = useState('gate');
  const [code, setCode] = useState('');
  const [instructions, setInstructions] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dirty = useRef(false);
  dirty.current = Boolean(code || instructions || type !== 'gate');
  useEffect(() => registerDirty('add-code', () => dirty.current), [registerDirty]);

  const trimmedCode = code.trim();
  // Soft-warn (don't block) if the same code+type already exists in this
  // community. A community could legitimately have two gate codes for two
  // different gates, so we don't hard-block — we just nudge.
  const duplicate = trimmedCode && existingCodes.some(
    (c) => c.type === type && c.code === trimmedCode,
  );
  const valid = trimmedCode && !busy;

  const submit = async (e) => {
    e?.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError('');
    try {
      const result = await workspace.createCode({
        type,
        code: trimmedCode,
        instructions: instructions.trim() || undefined,
      });
      if (result.ok) onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not add code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="border border-primary/30 bg-primary/5 rounded-lg p-2.5 mb-2 space-y-2.5"
      data-testid="add-code-form"
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
          Add Code
        </span>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-stone-400 hover:text-stone-700 transition-colors disabled:opacity-40"
          data-testid="add-code-cancel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Type + Code on one row */}
      <div className="grid grid-cols-[auto_1fr] gap-2 items-end">
        <div className="space-y-1">
          <label className="text-[10px] font-medium text-stone-600 uppercase tracking-wider">Type</label>
          <div className="inline-flex rounded-md border border-stone-300 overflow-hidden" data-testid="add-code-type">
            <button
              type="button"
              onClick={() => setType('gate')}
              disabled={busy}
              className={`px-2 py-1 text-[11px] font-medium inline-flex items-center gap-1 transition-colors ${
                type === 'gate'
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-white text-stone-500 hover:bg-stone-50'
              }`}
              data-testid="add-code-type-gate"
            >
              <KeyRound className="h-3 w-3" /> Gate
            </button>
            <button
              type="button"
              onClick={() => setType('door')}
              disabled={busy}
              className={`px-2 py-1 text-[11px] font-medium inline-flex items-center gap-1 border-l border-stone-300 transition-colors ${
                type === 'door'
                  ? 'bg-blue-50 text-blue-700'
                  : 'bg-white text-stone-500 hover:bg-stone-50'
              }`}
              data-testid="add-code-type-door"
            >
              <DoorClosed className="h-3 w-3" /> Door
            </button>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-medium text-stone-600 uppercase tracking-wider">Code</label>
          <input
            type="text"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={busy}
            placeholder="e.g. 1234"
            className="w-full text-sm font-mono border border-stone-300 rounded-md px-2 py-1 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-stone-50"
            data-testid="add-code-value"
          />
        </div>
      </div>

      {/* Instructions (optional) */}
      <div className="space-y-1">
        <label className="text-[10px] font-medium text-stone-600 uppercase tracking-wider">
          Instructions <span className="text-stone-400 normal-case font-normal">(optional)</span>
        </label>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          disabled={busy}
          rows={2}
          placeholder="e.g. dial 9 then 1234, buzz at the gatehouse"
          className="w-full text-xs border border-stone-300 rounded-md px-2 py-1 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-stone-50 resize-none"
          data-testid="add-code-instructions"
        />
      </div>

      {duplicate && !error && (
        <p className="text-[11px] text-amber-700 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          A {type} code <span className="font-mono">{trimmedCode}</span> already exists here. You can still add a duplicate if intentional.
        </p>
      )}
      {error && (
        <p className="text-[11px] text-red-600 flex items-center gap-1" data-testid="add-code-error">
          <AlertTriangle className="h-3 w-3" /> {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-2 py-1 text-[11px] font-medium rounded border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!valid}
          className="px-2.5 py-1 text-[11px] font-medium rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors inline-flex items-center gap-1"
          data-testid="add-code-submit"
        >
          {busy ? (
            <><Loader2 className="h-3 w-3 animate-spin" /> Adding…</>
          ) : (
            <><Plus className="h-3 w-3" /> Add code</>
          )}
        </button>
      </div>
    </form>
  );
}

// ── loaded detail ────────────────────────────────────────────────────────────

function CommunityDetail({
  data,
  workspace,
  showMap,
  onRename,
  onDeprecate,
  onRestore,
  onDeleteCode,
  onRemoveAddress,
  onDeleteCommunity,
  onFitMap,
  onGuardGatedToggle,
  onPatchCode,
  // Slice 3 plumbing (pending pins live in SettingsView)
  pendingPins,
  onPendingPinAdd,
  onPendingPinRemove,
  onAddAllPending,
}) {
  const { community_name, guard_gated = false, codes: rawCodes = [], addresses = [] } = data;
  const { registerDirty } = workspace;
  const mayDiscard = () => workspace.canLeave();
  const submittedForm = useRef(null);
  // Split active vs deprecated. Backend now always sends both because the
  // panel fetches with include_deprecated=true. Active codes keep all the
  // existing affordances; deprecated codes get a separate section with a
  // Restore button.
  const codes = rawCodes.filter((c) => !c.deprecated);
  const deprecatedCodes = rawCodes.filter((c) => c.deprecated);

  // ── guard-gated toggle state ──────────────────────────────────────────────
  const [guardBusy, setGuardBusy] = useState(false);
  const [guardErr, setGuardErr] = useState('');

  const toggleGuard = async () => {
    setGuardBusy(true);
    setGuardErr('');
    try {
      await onGuardGatedToggle?.(!guard_gated);
    } catch (e) {
      setGuardErr(e?.response?.data?.detail || 'Failed to update.');
    } finally {
      setGuardBusy(false);
    }
  };

  // ── add-code state ────────────────────────────────────────────────────────
  const [addingCode, setAddingCode] = useState(false);

  // ── delete-community state ───────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState('');

  const submitDelete = async () => {
    setDeleteBusy(true);
    setDeleteErr('');
    try {
      const result = await onDeleteCommunity();
      if (!result.ok) return;
      // SettingsView will clear `selectedCommunity` on success → this
      // component unmounts before we reach the next line. Resetting state
      // is defensive in case the parent decides not to unselect.
      setDeleteConfirm(false);
    } catch (e) {
      setDeleteErr(e?.response?.data?.detail || e?.message || 'Delete failed.');
    } finally {
      setDeleteBusy(false);
    }
  };

  // ── rename state ──────────────────────────────────────────────────────────
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameErr, setRenameErr] = useState('');
  const renameRef = useRef(null);

  const startRename = () => {
    if (renaming && !mayDiscard()) return;
    setRenameVal(community_name);
    setRenameErr('');
    setRenaming(true);
  };
  const cancelRename = () => { if (mayDiscard()) { submittedForm.current = null; setRenaming(false); setRenameErr(''); } };
  const submitRename = async () => {
    const trimmed = renameVal.trim();
    if (!trimmed || trimmed === community_name) { cancelRename(); return; }
    setRenameBusy(true);
    submittedForm.current = 'rename';
    try {
      const result = await onRename(trimmed);
      if (result.ok) setRenaming(false);
    } catch (e) {
      setRenameErr(e?.response?.data?.detail || 'Rename failed.');
    } finally {
      setRenameBusy(false);
    }
  };
  const onRenameKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); submitRename(); }
    if (e.key === 'Escape') cancelRename();
  };

  // ── edit state (per-code) ─────────────────────────────────────────────────
  const [editingId, setEditingId] = useState(null);
  const [editCodeVal, setEditCodeVal] = useState('');
  const [editInstructionsVal, setEditInstructionsVal] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState('');

  const startEdit = (c) => {
    if (editingId && !mayDiscard()) return;
    setEditingId(c.id);
    setEditCodeVal(c.code);
    setEditInstructionsVal(c.instructions || '');
    setEditErr('');
  };
  const cancelEdit = () => { if (mayDiscard()) { submittedForm.current = null; setEditingId(null); setEditErr(''); } };
  const submitEdit = async (codeId) => {
    const trimmedCode = editCodeVal.trim();
    if (!trimmedCode) { setEditErr('Code cannot be empty.'); return; }
    setEditBusy(true);
    submittedForm.current = 'edit-code';
    setEditErr('');
    try {
      const result = await onPatchCode(codeId, { code: trimmedCode, instructions: editInstructionsVal.trim() || null });
      if (result.ok) setEditingId(null);
    } catch (e) {
      setEditErr(e?.response?.data?.detail || 'Update failed.');
    } finally {
      setEditBusy(false);
    }
  };

  // ── deprecate state (per-code) ────────────────────────────────────────────
  const [deprecatingId, setDeprecatingId] = useState(null);
  const [deprecateReason, setDeprecateReason] = useState('');
  const [deprecateBusy, setDeprecateBusy] = useState(false);
  const [deprecateErr, setDeprecateErr] = useState('');

  const startDeprecate = (id) => { if (deprecatingId && !mayDiscard()) return; setDeprecatingId(id); setDeprecateReason(''); setDeprecateErr(''); };
  const cancelDeprecate = () => { if (mayDiscard()) { submittedForm.current = null; setDeprecatingId(null); setDeprecateErr(''); } };
  const submitDeprecate = async (id) => {
    const reason = deprecateReason.trim();
    if (!reason) { setDeprecateErr('Please enter a reason.'); return; }
    setDeprecateBusy(true);
    submittedForm.current = 'deprecate-code';
    try {
      const result = await onDeprecate(id, reason);
      if (result.ok) setDeprecatingId(null);
    } catch (e) {
      setDeprecateErr(e?.response?.data?.detail || 'Deprecation failed.');
    } finally {
      setDeprecateBusy(false);
    }
  };

  // ── remove-address state (per-place_id) ───────────────────────────────────
  // No inline confirmation — clicking the trash icon removes immediately.
  // Row feedback is local; the workspace serializes all community mutations.
  const [removingId, setRemovingId] = useState(null);
  const [removeErrById, setRemoveErrById] = useState({});

  const submitRemove = async (placeId) => {
    setRemovingId(placeId);
    setRemoveErrById((prev) => {
      const { [placeId]: _ignored, ...rest } = prev;
      return rest;
    });
    try {
      await onRemoveAddress(placeId);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Remove failed.';
      setRemoveErrById((prev) => ({ ...prev, [placeId]: msg }));
    } finally {
      setRemovingId(null);
    }
  };

  // ── restore-code state (per-id) ───────────────────────────────────────────
  // Inline like remove-address: tap Restore, the row spins, error stays
  // attached to that one row if it fails.
  const [restoringId, setRestoringId] = useState(null);
  const [restoreErrById, setRestoreErrById] = useState({});

  const submitRestore = async (codeId) => {
    setRestoringId(codeId);
    setRestoreErrById((prev) => {
      const { [codeId]: _ignored, ...rest } = prev;
      return rest;
    });
    try {
      await onRestore(codeId);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Restore failed.';
      setRestoreErrById((prev) => ({ ...prev, [codeId]: msg }));
    } finally {
      setRestoringId(null);
    }
  };

  // ── delete-deprecated-code state (per-id) ────────────────────────────────
  // Two-tap pattern: first tap arms the row (confirmId set); second tap
  // commits. ESC / clicking elsewhere on the row disarms. Mirrors the
  // "Delete this community" danger-zone pattern but inline per code.
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteErrById, setDeleteErrById] = useState({});

  const submitDeleteCode = async (codeId) => {
    setDeletingId(codeId);
    setDeleteErrById((prev) => {
      const { [codeId]: _ignored, ...rest } = prev;
      return rest;
    });
    try {
      const result = await onDeleteCode(codeId);
      if (result.ok) setConfirmDeleteId(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Delete failed.';
      setDeleteErrById((prev) => ({ ...prev, [codeId]: msg }));
    } finally {
      setDeletingId(null);
    }
  };

  // ── bulk add state (Slice 3) ──────────────────────────────────────────────
  const [addAllBusy, setAddAllBusy] = useState(false);
  const [addAllErr, setAddAllErr] = useState('');
  const submitAddAll = async () => {
    setAddAllBusy(true);
    setAddAllErr('');
    try {
      await onAddAllPending();
    } catch (e) {
      setAddAllErr(e?.response?.data?.detail || e?.message || 'Add all failed.');
    } finally {
      setAddAllBusy(false);
    }
  };

  const dirty = useRef(false);
  dirty.current = Boolean(renaming || editingId || deprecatingId);
  useEffect(() => registerDirty('community-detail', () => dirty.current), [registerDirty]);
  useEffect(() => {
    const completed = workspace.completedOperation;
    if (!completed || completed.communityId !== workspace.selectedCommunityId) return;
    if (completed.kind === 'update-community' && submittedForm.current === 'rename') setRenaming(false);
    if (completed.kind === 'update-code' && submittedForm.current === 'edit-code') setEditingId(null);
    if (completed.kind === 'update-code' && submittedForm.current === 'deprecate-code') setDeprecatingId(null);
    if (completed.kind === 'create-code') setAddingCode(false);
    if (completed.kind === 'delete-code') setConfirmDeleteId(null);
    submittedForm.current = null;
  }, [workspace.completedOperation, workspace.selectedCommunityId]);
  useEffect(() => {
    // Returning a rejected request to editing is not a submission. A later,
    // unrelated save must not close this retained draft.
    if (workspace.status === 'idle' && !workspace.busy && !workspace.recovery) submittedForm.current = null;
  }, [workspace.status, workspace.busy, workspace.recovery]);

  // Derived sets for dedup in search/map pickers
  const existingPlaceIds = new Set(addresses.map((a) => a.place_id).filter(Boolean));
  const pendingPlaceIds = new Set(pendingPins.map((p) => p.place_id));

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <fieldset disabled={workspace.readOnly} className="min-w-0 p-4 lg:p-6 space-y-5" data-testid="community-detail-loaded">

      {/* ── Community name (inline rename) ── */}
      <div className="flex items-start gap-2">
        <Building2 className="h-5 w-5 text-stone-400 flex-shrink-0 mt-0.5" />
        {renaming ? (
          <div className="flex-1 space-y-1">
            <input
              ref={renameRef}
              autoFocus
              value={renameVal}
              onChange={(e) => setRenameVal(e.target.value)}
              onKeyDown={onRenameKey}
              disabled={renameBusy}
              className="w-full text-base font-semibold text-stone-900 border border-primary rounded-md px-2 py-1 outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
              data-testid="community-rename-input"
            />
            <p className="text-[10px] text-stone-400">Enter to save · Esc to cancel</p>
            <div className="flex gap-2 text-xs">
              <button type="button" disabled={renameBusy || !renameVal.trim()} onClick={submitRename} data-testid="community-rename-save">Save name</button>
              <button type="button" disabled={renameBusy} onClick={cancelRename} data-testid="community-rename-cancel">Cancel</button>
            </div>
            {renameErr && (
              <p className="text-[11px] text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />{renameErr}
              </p>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 -ml-1">
              <button
                onClick={startRename}
                className="group flex items-center gap-1.5 text-left rounded-lg px-1 hover:bg-stone-100 transition-colors"
                data-testid="community-rename-trigger"
              >
                <h2 className="text-base font-semibold text-stone-900">{community_name}</h2>
                <Pencil className="h-3.5 w-3.5 text-stone-300 group-hover:text-stone-500 transition-colors" />
              </button>
              {onFitMap && addresses.length > 0 && (
                <button
                  type="button"
                  onClick={onFitMap}
                  title="Center map on community addresses"
                  className="p-1 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded transition-colors"
                  data-testid="community-fit-map-btn"
                >
                  <Crosshair className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={toggleGuard}
              disabled={guardBusy}
              title={guard_gated ? 'Guard-gated — click to remove' : 'Mark as guard-gated'}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold flex-shrink-0 transition-colors disabled:opacity-50 ${
                guard_gated
                  ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
                  : 'bg-stone-50 text-stone-400 border-stone-200 hover:bg-stone-100 hover:text-stone-600'
              }`}
              data-testid="community-guard-gated-toggle"
            >
              <UserRound className="h-3 w-3" />
              Guard
            </button>
          </div>
        )}
      </div>
      {guardErr && (
        <p className="text-[11px] text-red-600 flex items-center gap-1 ml-7">
          <AlertTriangle className="h-3 w-3" />{guardErr}
        </p>
      )}

      {/* ── Codes ── */}
      <section>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5">
            <Key className="h-3.5 w-3.5 text-stone-400" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">
              Codes ({codes.length})
            </span>
          </div>
          {!addingCode && (
            <button
              type="button"
              onClick={() => setAddingCode(true)}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md text-primary hover:bg-primary/10 transition-colors"
              data-testid="add-code-trigger"
            >
              <Plus className="h-3 w-3" /> Add code
            </button>
          )}
        </div>

        {addingCode && (
          <AddCodeForm
            workspace={workspace}
            existingCodes={codes}
            onCancel={() => { if (mayDiscard()) setAddingCode(false); }}
            onCreated={() => setAddingCode(false)}
          />
        )}

        {codes.length === 0 ? (
          <p className="text-xs text-stone-400">No active codes.</p>
        ) : (
          <ul className="space-y-2" data-testid="community-detail-codes">
            {codes.map((c) => (
              <li
                key={c.id}
                className="rounded-lg border border-stone-200 px-3 py-2.5 bg-white space-y-2"
                data-testid={`community-code-${c.id}`}
              >
                {editingId === c.id ? (
                  /* ── inline edit form ── */
                  <div className="space-y-2" data-testid={`community-code-edit-form-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">Edit Code</span>
                      <button type="button" onClick={cancelEdit} disabled={editBusy} className="text-stone-400 hover:text-stone-700 transition-colors disabled:opacity-40">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium text-stone-600 uppercase tracking-wider">Code</label>
                      <input
                        autoFocus
                        type="text"
                        value={editCodeVal}
                        onChange={(e) => setEditCodeVal(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitEdit(c.id); } if (e.key === 'Escape') cancelEdit(); }}
                        disabled={editBusy}
                        className="w-full text-sm font-mono border border-stone-300 rounded-md px-2 py-1 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-stone-50"
                        data-testid={`community-code-edit-value-${c.id}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium text-stone-600 uppercase tracking-wider">
                        Instructions <span className="text-stone-400 normal-case font-normal">(optional)</span>
                      </label>
                      <textarea
                        value={editInstructionsVal}
                        onChange={(e) => setEditInstructionsVal(e.target.value)}
                        disabled={editBusy}
                        rows={2}
                        placeholder="e.g. dial 9 then 1234, buzz at the gatehouse"
                        className="w-full text-xs border border-stone-300 rounded-md px-2 py-1 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-stone-50 resize-none"
                        data-testid={`community-code-edit-instructions-${c.id}`}
                      />
                    </div>
                    {editErr && (
                      <p className="text-[11px] text-red-600 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />{editErr}
                      </p>
                    )}
                    <div className="flex gap-2 justify-end">
                      <button type="button" onClick={cancelEdit} disabled={editBusy} className="px-2 py-1 text-[11px] font-medium rounded border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40 transition-colors">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => submitEdit(c.id)}
                        disabled={editBusy || !editCodeVal.trim()}
                        className="px-2.5 py-1 text-[11px] font-medium rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors inline-flex items-center gap-1"
                        data-testid={`community-code-edit-submit-${c.id}`}
                      >
                        {editBusy ? <><Loader2 className="h-3 w-3 animate-spin" /> Saving…</> : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                {/* Code header row */}
                <div className="flex items-center gap-2 flex-wrap">
                  <CodeTypeBadge type={c.type} />
                  <span className="font-mono text-sm font-semibold text-stone-900">{c.code}</span>
                  <button
                    type="button"
                    onClick={() => startEdit(c)}
                    className="ml-auto p-1 rounded text-stone-300 hover:text-stone-600 hover:bg-stone-100 transition-colors"
                    title="Edit code"
                    data-testid={`community-code-edit-btn-${c.id}`}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </div>

                {c.instructions && (
                  <p className="text-xs text-stone-500 leading-relaxed">{c.instructions}</p>
                )}

                <div className="flex items-center gap-3">
                  {c.verified_at && (
                    <span className="flex items-center gap-1 text-[10px] text-stone-400">
                      <Shield className="h-3 w-3" />
                      Verified {relativeTime(c.verified_at)}{c.verified_by ? ` by ${c.verified_by}` : ''}
                    </span>
                  )}
                  {c.last_used_at && (
                    <span className="flex items-center gap-1 text-[10px] text-stone-400">
                      <Clock className="h-3 w-3" />
                      Used {relativeTime(c.last_used_at)}
                    </span>
                  )}
                </div>

                {/* Deprecate form / button */}
                {deprecatingId === c.id ? (
                  <div className="pt-2 border-t border-stone-100 space-y-2" data-testid={`community-code-deprecate-form-${c.id}`}>
                    <input
                      autoFocus
                      placeholder="Reason for deprecation…"
                      value={deprecateReason}
                      onChange={(e) => setDeprecateReason(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && submitDeprecate(c.id)}
                      disabled={deprecateBusy}
                      className="w-full text-xs border border-stone-300 rounded px-2 py-1.5 outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-200 disabled:opacity-50"
                      data-testid={`community-code-deprecate-reason-${c.id}`}
                    />
                    {deprecateErr && (
                      <p className="text-[11px] text-red-600 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />{deprecateErr}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => submitDeprecate(c.id)}
                        disabled={deprecateBusy || !deprecateReason.trim()}
                        className="px-2.5 py-1 text-[11px] font-medium rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 transition-colors"
                        data-testid={`community-code-deprecate-submit-${c.id}`}
                      >
                        {deprecateBusy ? 'Deprecating…' : 'Confirm Deprecation'}
                      </button>
                      <button
                        onClick={cancelDeprecate}
                        disabled={deprecateBusy}
                        className="px-2.5 py-1 text-[11px] font-medium rounded border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40 transition-colors"
                        data-testid={`community-code-deprecate-cancel-${c.id}`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="pt-1.5 border-t border-stone-100">
                    <button
                      onClick={() => startDeprecate(c.id)}
                      className="text-[11px] text-stone-400 hover:text-rose-600 transition-colors"
                      data-testid={`community-code-deprecate-btn-${c.id}`}
                    >
                      Deprecate this code
                    </button>
                  </div>
                )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Deprecated codes (Restore-from-Purgatory) ── */}
      {deprecatedCodes.length > 0 && (
        <section data-testid="community-deprecated-codes-section">
          <div className="flex items-center gap-1.5 mb-2">
            <Key className="h-3.5 w-3.5 text-stone-300" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">
              Deprecated codes ({deprecatedCodes.length})
            </span>
          </div>
          <p className="text-[11px] text-stone-400 mb-2 leading-relaxed">
            These codes were flagged as not working and are hidden from drivers.
            Restore one if it's verified working again.
          </p>
          <ul className="space-y-2">
            {deprecatedCodes.map((c) => {
              const isRestoring = restoringId === c.id;
              const rowErr = restoreErrById[c.id];
              return (
                <li
                  key={c.id}
                  className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 space-y-2 opacity-80"
                  data-testid={`community-deprecated-code-${c.id}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <CodeTypeBadge type={c.type} />
                    <span className="font-mono text-sm font-semibold text-stone-500 line-through">
                      {c.code}
                    </span>
                  </div>

                  {c.deprecated_reason && (
                    <p
                      className="text-xs text-stone-500 leading-relaxed italic"
                      data-testid={`community-deprecated-code-reason-${c.id}`}
                    >
                      {c.deprecated_reason}
                    </p>
                  )}

                  {c.instructions && (
                    <p className="text-xs text-stone-400 leading-relaxed line-through">
                      {c.instructions}
                    </p>
                  )}

                  <div className="pt-1.5 border-t border-stone-200 flex items-center justify-between gap-2">
                    <span className="text-[10px] text-stone-400">
                      {c.last_used_at
                        ? `Last used ${relativeTime(c.last_used_at)}`
                        : 'Never used'}
                    </span>
                    {confirmDeleteId === c.id ? (
                      <div
                        className="flex items-center gap-1.5"
                        data-testid={`community-code-delete-confirm-${c.id}`}
                      >
                        <span className="text-[10px] text-rose-700">Permanently delete?</span>
                        <button
                          onClick={() => submitDeleteCode(c.id)}
                          disabled={deletingId === c.id}
                          className="px-2 py-1 text-[11px] font-medium rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 transition-colors inline-flex items-center gap-1"
                          data-testid={`community-code-delete-submit-${c.id}`}
                        >
                          {deletingId === c.id ? (
                            <><Loader2 className="h-3 w-3 animate-spin" /> Deleting…</>
                          ) : (
                            <>Yes, delete</>
                          )}
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          disabled={deletingId === c.id}
                          className="px-2 py-1 text-[11px] font-medium rounded border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40 transition-colors"
                          data-testid={`community-code-delete-cancel-${c.id}`}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setConfirmDeleteId(c.id)}
                          disabled={isRestoring}
                          className="text-[11px] text-stone-400 hover:text-rose-600 transition-colors disabled:opacity-40"
                          title="Permanently delete this code"
                          data-testid={`community-code-delete-btn-${c.id}`}
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => submitRestore(c.id)}
                          disabled={isRestoring}
                          className="px-2.5 py-1 text-[11px] font-medium rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 transition-colors inline-flex items-center gap-1"
                          data-testid={`community-code-restore-btn-${c.id}`}
                        >
                          {isRestoring ? (
                            <><Loader2 className="h-3 w-3 animate-spin" /> Restoring…</>
                          ) : (
                            <>Restore</>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  {rowErr && (
                    <p
                      className="text-[11px] text-red-600 flex items-center gap-1"
                      data-testid={`community-code-restore-error-${c.id}`}
                    >
                      <AlertTriangle className="h-3 w-3" />{rowErr}
                    </p>
                  )}
                  {deleteErrById[c.id] && (
                    <p
                      className="text-[11px] text-red-600 flex items-center gap-1"
                      data-testid={`community-code-delete-error-${c.id}`}
                    >
                      <AlertTriangle className="h-3 w-3" />{deleteErrById[c.id]}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* ── Addresses ── */}
      <section>
        <div className="flex items-center gap-1.5 mb-2">
          <MapPin className="h-3.5 w-3.5 text-stone-400" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">
            Addresses ({addresses.length})
          </span>
        </div>

        {/* Search bar to stage a pending pin */}
        <div className="mb-2" data-testid="community-address-search">
          <AddressSearchBar
            existingPlaceIds={existingPlaceIds}
            pendingPlaceIds={pendingPlaceIds}
            onPick={onPendingPinAdd}
            disabled={addAllBusy || workspace.readOnly}
            captureSelection={workspace.captureSelection}
            isCurrent={workspace.isCurrent}
          />
          <p className="text-[10px] text-stone-400 mt-1">
            Tip: you can also click buildings on the map to stage them.
          </p>
        </div>

        {/* Pending pins staging area */}
        {pendingPins.length > 0 && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 p-2 mb-2 space-y-1.5"
            data-testid="community-pending-pins"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                Staged ({pendingPins.length})
              </span>
              <button
                onClick={submitAddAll}
                disabled={addAllBusy}
                className="px-2.5 py-1 text-[11px] font-medium rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 transition-colors flex items-center gap-1"
                data-testid="community-pending-pins-add-all"
              >
                {addAllBusy ? (
                  <><Loader2 className="h-3 w-3 animate-spin" /> Adding…</>
                ) : (
                  <><Plus className="h-3 w-3" /> Add all</>
                )}
              </button>
            </div>
            <ul className="space-y-1">
              {pendingPins.map((p) => (
                <li
                  key={p.tempId}
                  className="flex items-start gap-2 text-xs"
                  data-testid={`community-pending-pin-${p.tempId}`}
                >
                  <MapPin className="h-3 w-3 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="flex-1 text-stone-700 leading-relaxed break-words">
                    {p.formatted_address || (
                      <span className="text-stone-400 italic">Unresolved address</span>
                    )}
                  </span>
                  <button
                    onClick={() => onPendingPinRemove(p.tempId)}
                    disabled={addAllBusy}
                    className="text-amber-700/60 hover:text-rose-600 disabled:opacity-40 transition-colors"
                    title="Remove from staged list"
                    data-testid={`community-pending-pin-remove-${p.tempId}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
            {addAllErr && (
              <p className="text-[11px] text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />{addAllErr}
              </p>
            )}
          </div>
        )}

        {addresses.length === 0 && pendingPins.length === 0 ? (
          <p className="text-xs text-stone-400">No addresses linked yet.</p>
        ) : (
          <div className="space-y-3" data-testid="community-detail-addresses">
            {groupAddressesByStreet(addresses).map(([streetName, items]) => (
              <div key={streetName} data-testid={`community-address-group-${streetName}`}>
                <div
                  className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1"
                  data-testid={`community-address-group-header-${streetName}`}
                >
                  {streetName}
                  <span className="ml-1.5 text-[10px] font-normal text-stone-400 normal-case">
                    ({items.length})
                  </span>
                </div>
                <ul className="space-y-1">
                  {items.map((a) => {
                    const isRemoving = removingId === a.place_id;
                    const rowErr = removeErrById[a.place_id];
                    return (
                      <li key={a.place_id} className="space-y-1" data-testid={`community-address-${a.place_id}`}>
                        <div className="flex items-start gap-2 group">
                          <MapPin className="h-3 w-3 text-stone-300 mt-0.5 flex-shrink-0" />
                          <span className="flex-1 text-xs text-stone-700 leading-relaxed">
                            {a.formatted_address || (
                              <span className="text-stone-400 italic">Address not yet geocoded</span>
                            )}
                          </span>
                          <button
                            onClick={() => submitRemove(a.place_id)}
                            disabled={isRemoving}
                            className="transition-colors text-stone-300 hover:text-rose-500 disabled:opacity-40 disabled:cursor-wait"
                            title="Remove address from community"
                            data-testid={`community-address-remove-btn-${a.place_id}`}
                          >
                            {isRemoving ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                        {rowErr && (
                          <div className="ml-5 text-[11px] text-red-600 flex items-center gap-1" data-testid={`community-address-remove-error-${a.place_id}`}>
                            <AlertTriangle className="h-3 w-3" />{rowErr}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Danger zone: delete community ── */}
      <section className="pt-3 border-t border-stone-100" data-testid="community-delete-zone">
        {!deleteConfirm ? (
          <button
            type="button"
            onClick={() => { setDeleteConfirm(true); setDeleteErr(''); }}
            className="text-[11px] text-stone-400 hover:text-rose-600 transition-colors"
            data-testid="community-delete-trigger"
          >
            Delete this community
          </button>
        ) : (
          <div
            className="rounded-lg border border-rose-300 bg-rose-50 p-3 space-y-2"
            data-testid="community-delete-confirm"
          >
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">
                Permanently delete community
              </span>
            </div>
            <p className="text-xs text-stone-700 leading-relaxed">
              This deletes <span className="font-semibold">{community_name}</span> and all{' '}
              <span className="font-semibold">{codes.length}</span>{' '}
              {codes.length === 1 ? 'code' : 'codes'} under it.
            </p>
            {addresses.length > 0 && (
              <p className="text-xs text-rose-700 leading-relaxed" data-testid="community-delete-warning-addresses">
                <span className="font-semibold">{addresses.length}</span>{' '}
                {addresses.length === 1 ? 'address is' : 'addresses are'} currently linked.
                They will be disassociated before the codes are removed. The
                addresses themselves are not deleted from other orders.
              </p>
            )}
            <p className="text-[11px] text-stone-500 italic">
              This action cannot be undone.
            </p>
            {deleteErr && (
              <p className="text-[11px] text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> {deleteErr}
              </p>
            )}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={submitDelete}
                disabled={deleteBusy}
                className="px-2.5 py-1 text-[11px] font-medium rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 transition-colors inline-flex items-center gap-1"
                data-testid="community-delete-submit"
              >
                {deleteBusy ? (
                  <><Loader2 className="h-3 w-3 animate-spin" /> Deleting…</>
                ) : (
                  <><Trash2 className="h-3 w-3" /> Delete permanently</>
                )}
              </button>
              <button
                type="button"
                onClick={() => { setDeleteConfirm(false); setDeleteErr(''); }}
                disabled={deleteBusy}
                className="px-2.5 py-1 text-[11px] font-medium rounded border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40 transition-colors"
                data-testid="community-delete-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── Inline map (only when showMap=true, i.e. narrow 2-col layout) ── */}
      {showMap && (addresses.length > 0 || pendingPins.length > 0) && (
        <CommunityMap
          addresses={addresses}
          pendingPins={pendingPins}
          onPendingPinAdd={workspace.readOnly ? undefined : onPendingPinAdd}
          selectionKey={workspace.opening}
          captureSelection={workspace.captureSelection}
          isCurrentSelection={workspace.isCurrent}
        />
      )}
    </fieldset>
  );
}

// ── main component ───────────────────────────────────────────────────────────

export default function CommunityDetailPanel({ workspace, showMap = true, onFitMap }) {
  const { selectedCommunityId, detail: data, detailLoading: loading, detailError: error } = workspace;
  if (!selectedCommunityId) return <BlankCanvas />;
  if (!data && loading) {
    return <div className="p-6" data-testid="community-detail-loading" role="status">Loading community…</div>;
  }
  if (!data && !error) return <BlankCanvas />;
  return (
    <>
      {error && <div className="p-4 text-sm text-red-600" role="alert" data-testid="community-detail-error">
        {error} <button type="button" disabled={workspace.busy} onClick={() => workspace.refresh()}>Retry loading community</button>
      </div>}
      {data && <CommunityDetail
        key={workspace.opening}
        data={data}
        workspace={workspace}
        showMap={showMap}
        onRename={(name) => workspace.updateCommunity({ name })}
        onGuardGatedToggle={(guard_gated) => workspace.updateCommunity({ guard_gated })}
        onDeprecate={(id, reason) => workspace.updateCode(id, { deprecated: true, deprecated_reason: reason })}
        onRestore={(id) => workspace.updateCode(id, { deprecated: false })}
        onDeleteCode={workspace.deleteCode}
        onRemoveAddress={workspace.removeAddress}
        onPatchCode={workspace.updateCode}
        onDeleteCommunity={workspace.deleteCommunity}
        onFitMap={onFitMap}
        pendingPins={workspace.pendingPins}
        onPendingPinAdd={workspace.addPendingPin}
        onPendingPinRemove={workspace.removePendingPin}
        onAddAllPending={workspace.addAllPending}
      />}
    </>
  );
}
